<template>
  <VcLayoutWithRightSidebar is-sidebar-sticky class="gap-5 lg:gap-10">
    <ViewCartItems />

    <div v-for="(step, index) in props.steps" :key="index" class="flex flex-col gap-4">
      <VcWidget>
        <div
          class="-mt-4 flex items-center justify-between gap-2 pt-4 md:-mx-5 md:px-5"
          :class="{
            'mb-4 rounded-t-md border-b border-b-[--color-neutral-a4] bg-transparent pb-4 text-[--color-neutral-a1] lg:border-b-0 lg:bg-[--color-neutral-a6]':
              isActiveStep(index),
            'pb-4': isCompletedStep(index),
          }"
        >
          <h6
            class="flex items-center gap-2 text-lg font-bold"
            :class="{
              'text-[--color-neutral-a1] !opacity-100': isActiveStep(index),
              'text-[--color-neutral-a1] opacity-75': !isCompletedStep(index) && isDisabledStep(step, index),
            }"
          >
            <VcIcon
              :name="isCompletedStep(index) ? 'check-circle-alt' : step.icon"
              :class="[
                {
                  'text-[--color-primary-a1] !opacity-100': isActiveStep(index),
                  'text-[--color-alert-d0]': isCompletedStep(index),
                  'text-[--color-neutral-a1] opacity-75': !isCompletedStep(index) && isDisabledStep(step, index),
                },
              ]"
              :size="20"
            ></VcIcon>

            {{ $t(step.text) }}
          </h6>
          <VcButton
            v-if="isCompletedStep(index)"
            :to="step.component"
            type="button"
            variant="link"
            size="lg"
            class="text-[--link-color] underline"
            @click="toggleSection(step.id!)"
            >{{ $t("common.buttons.edit") }}
          </VcButton>
        </div>
        <component :is="step.component">
          <ShippingAddress
            v-if="step.component === 'ShippingAddress'"
            :is-completed-step="isCompletedStep(index)"
            :is-active-step="isActiveStep(index)"
            :next-step="getNextStep(index)"
          />
          <ShippingMethod
            v-if="step.component === 'ShippingMethod'"
            :is-active-step="isActiveStep(index)"
            :is-completed-step="isCompletedStep(index)"
            :next-step="getNextStep(index)"
          />
          <ExportItems
            v-if="step.component === 'ExportItems'"
            :is-active-step="isActiveStep(index)"
            :is-completed-step="isCompletedStep(index)"
            :next-step="getNextStep(index)"
            class="pb-4"
          />
          <PaymentSection
            v-if="step.component === 'Payment' && isActiveStep(index)"
            :is-active-step="isActiveStep(index)"
            :is-completed-step="isCompletedStep(index)"
            :next-step="getNextStep(index)"
            @payment-information-entered="handlePaymentInformationEntered"
          />
        </component>
      </VcWidget>
    </div>

    <PlaceOrder />
    <template #sidebar>
      <OrderSummary
        :cart="cart!"
        :selected-items="selectedLineItems"
        show-tax-and-shipping
        footnote
        class="order-summary__checkout order-3 lg:order-none"
      >
        <VcButton type="button" color="primary" variant="solid" size="lg" class="disabled-button w-full">{{
          $t("common.buttons.place_order")
        }}</VcButton>
        <template #header>
          <div class="order-summary__message mt-5 text-xs font-normal text-gray-400 lg:my-5 lg:border-b lg:pb-5">
            <span
              v-html-safe="$t('shared.checkout.agreement')"
              class="policy-link text-xs leading-5 text-[--color-neutral-a2]"
            ></span>
          </div>
        </template>
        <template #footer>
          <transition name="slide-fade-top" mode="out-in" appear>
            <VcAlert v-show="!isValidShipment" color="warning" size="sm" variant="outline-light" class="my-4" icon>
              {{ $t("common.messages.fill_all_required") }}
            </VcAlert>
          </transition>

          <transition name="slide-fade-top" mode="out-in" appear>
            <VcAlert v-show="hasValidationErrors" color="warning" size="sm" variant="solid-light" class="mt-4" icon>
              {{ $t("common.messages.something_went_wrong") }}
            </VcAlert>
          </transition>
        </template>
      </OrderSummary>

      <OptionalFields />
      <AccessoryItems />
    </template>
  </VcLayoutWithRightSidebar>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useFullCart } from "@/shared/cart";
import { OrderSummary, useCheckout } from "@/shared/checkout";
import AccessoryItems from "@/shared/checkout/components/accessory-items.vue";
import OptionalFields from "@/shared/checkout/components/optional-fields.vue";
import PlaceOrder from "@/shared/checkout/components/place-order.vue";
import ExportItems from "@/shared/checkout/components/steps/export-items.vue";
import PaymentSection from "@/shared/checkout/components/steps/payment.vue";
import ShippingAddress from "@/shared/checkout/components/steps/shipping-address.vue";
import ShippingMethod from "@/shared/checkout/components/steps/shipping-method.vue";
import ViewCartItems from "@/shared/checkout/components/view-checkout-items.vue";

const props = withDefaults(defineProps<IProps>(), {
  steps: () => [],
  currentStepIndex: 0,
  startStepIndex: 0,
});
const { cart, selectedLineItems, hasValidationErrors } = useFullCart();
const { isValidShipment } = useCheckout();
const hasCheckoutCompleted = ref(false);
// const ga = useGoogleAnalytics();

const isOptionalFieldsExpanded = ref(false);

interface IProps {
  steps?: IStepsItem[];
  currentStepIndex?: number;
  startStepIndex?: number;
  disabled?: boolean;
  transitionName?: string;
}

function isActiveStep(index: number) {
  return index + props.startStepIndex === props.currentStepIndex;
}

function isCompletedStep(index: number) {
  return index + props.startStepIndex < props.currentStepIndex || hasCheckoutCompleted.value;
}

function isDisabledStep(step: IStepsItem, index: number) {
  const isBeforeCurrentStep = index + props.startStepIndex < props.currentStepIndex;
  return step.disabled || props.disabled || !isBeforeCurrentStep;
}

const isShippingAddressExpanded = ref(false);

// Function to toggle specific sections
const toggleSection = function (section: string) {
  if (section === "Shipping") {
    isShippingAddressExpanded.value = !isShippingAddressExpanded.value;
  } else if (section === "optionalFields") {
    isOptionalFieldsExpanded.value = !isOptionalFieldsExpanded.value;
  }
};

function getNextStep(index: number) {
  return index + 1 < props.steps.length ? props.steps[index + 1].component : "placeOrder";
}

function handlePaymentInformationEntered() {
  hasCheckoutCompleted.value = true;
}
</script>

<style lang="scss">
.vc-steps {
  $itemActive: "";
  $itemCompleted: "";
  $itemDisabled: "";

  @apply flex flex-wrap gap-x-5 gap-y-2.5;

  &__item {
    &--active {
      $itemActive: &;
    }

    &--completed {
      $itemCompleted: &;
    }

    &--disabled {
      $itemDisabled: &;
    }
  }

  &__step {
    @apply flex items-center gap-1.5;
  }

  &__icon {
    @apply flex shrink-0 items-center justify-center w-5 h-5 rounded-full text-sm font-extrabold text-additional-50 bg-neutral-400;

    #{$itemActive}:not(#{$itemDisabled}) & {
      @apply bg-primary;
    }

    #{$itemCompleted}:not(#{$itemDisabled}) & {
      @apply bg-success;
    }
  }

  &__text {
    @apply text-sm font-semibold text-neutral-600;

    #{$itemActive}:not(#{$itemDisabled}) & {
      @apply text-neutral-900;
    }

    #{$itemCompleted}:not(#{$itemDisabled}) & {
      @apply text-success;
    }
  }
}

.checkout-page {
  .vc-layout-with-right-sidebar {
    &__sidebar {
      @apply md:w-[400px];
    }

    #order-summary {
      box-shadow: 0px 10px 20px 0px rgba(92, 115, 160, 0.07);

      .vc-alert {
        @apply my-0 lg:my-4;
      }
    }

    .next-step-btn {
      @apply md:min-w-[184px];
    }

    &__main {
      .place-order-btn {
        @apply hidden lg:block #{!important};
      }
    }
  }

  .order-summary {
    &__checkout {
      .vc-widget__slot {
        @apply flex flex-col;
      }
    }

    &__body {
      @apply order-1 lg:order-none pb-5 md:py-5 lg:pt-0 lg:pb-4;
    }

    &__message {
      @apply order-5 lg:order-none;
    }
  }

  .policy-message {
    @apply order-3 lg:order-none;
  }

  .total {
    @apply order-2 lg:order-none border-b pb-5 lg:border-none lg:pb-0 lg:mb-0;
  }

  .optional-fields-section {
    .vc-button {
      &__content {
        @apply w-full flex;
      }

      &__slot {
        @apply justify-between w-full items-start;
      }
    }
  }

  .disabled-button {
    &.vc-button--disabled {
      @apply border border-[--color-neutral-a3] text-[--color-neutral-a1] text-base #{!important};
    }
  }

  .vc-textarea__input {
    @apply py-3 pl-5 pr-4 rounded-md h-24 mb-1.5;

    &::placeholder {
      @apply text-sm text-[--color-neutral-a3];
    }
  }

  .option-input,
  .accessory-items {
    .vc-input__input {
      &::placeholder {
        @apply text-[--color-neutral-a2] leading-6;
      }
    }
  }

  .accessory-items {
    .vc-select .vc-dropdown-menu__list {
      @apply max-h-max #{!important};
    }
  }
}
</style>
